import { faColumns, faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useEffect, useState, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Spinner, Dropdown } from 'react-bootstrap';
import ColumnVisibilityToggle from 'components/common/ColumnVisibilityToggle';
import TransactionsTable, { transactionsTableColumns } from 'components/tables/TransactionsTable';
import useAdvanceTable from 'hooks/products/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { PaginationState, VisibilityState } from '@tanstack/react-table';
import { Transaction, useTransactionsData } from 'hooks/transactions/useTransactionsData';
import TransactionFilters from 'components/transactions/TransactionFilters';
import ToastManager from 'components/orders/ToastManager';
import ErrorNotification from 'components/orders/ErrorNotification';
import TransactionKpiCards from 'components/transactions/TransactionKpiCards';

// Import animation utilities
import { simulateTyping, simulateEnterPress } from 'utils/animationUtils';

// Import SCSS for animations
import 'assets/scss/orders/animations.scss';

/**
 * Transactions Component - Displays a list of seller transactions with filtering and sorting
 */
const Transactions = () => {
  const { t } = useTranslation();
  // State for column visibility
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    transaction_id: true,
    bank_transaction_id: true,
    message: true,
    formatted_date: true,
    transaction_amount: true,
    total_amount: true,
    comission_amount: true
  });
  
  // Get location to access state from the Link component
  const location = useLocation();
  
  // Reference to the search input for animations
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Search states
  const [searchValue, setSearchValue] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");
  
  // Date filter states
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  
  // Sort states
  const [sortTransactionId, setSortTransactionId] = useState<number | null>(null);
  const [sortTransactionDate, setSortTransactionDate] = useState<number | null>(null);
  const [sortTotalAmount, setSortTotalAmount] = useState<number | null>(null);
  
  // Error state
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  // Memoize the filter options to prevent unnecessary rerenders
  const filterOptions = useMemo(() => ({
    search: submittedSearch,
    fromDate,
    toDate
  }), [
    submittedSearch,
    fromDate,
    toDate
  ]);

  // Memoize the sort options to prevent unnecessary rerenders
  const sortOptions = useMemo(() => ({
    transactionId: sortTransactionId || undefined,
    transactionDate: sortTransactionDate || undefined,
    totalAmount: sortTotalAmount || undefined
  }), [sortTransactionId, sortTransactionDate, sortTotalAmount]);

  // Memoize the pagination options to prevent unnecessary rerenders
  const paginationOptions = useMemo(() => ({
    initialPageIndex: 0,
    initialPageSize: 10
  }), []);

  // Use our custom hook for transactions data with memoized options
  const { 
    tableData, 
    loading, 
    error,
    paginationCount,
    pagination, 
    handlePageChange, 
    handlePageSizeChange, 
    fetchTransactions,
    orderTotals
  } = useTransactionsData(filterOptions, sortOptions, paginationOptions);
  
  // Interface for column options
  interface ColumnOption {
    id: string;
    label: string;
    visible: boolean;
  }
  
  // Convert columns to options for the column toggler
  const columnOptions = useMemo<ColumnOption[]>(() => {
    return transactionsTableColumns.map(column => {
      const columnId = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      
      return {
        id: columnId,
        label: column.header as string,
        visible: columnVisibility[columnId] !== false
      };
    });
  }, [columnVisibility]);
  
  // Handler for toggling column visibility
  const handleToggleColumn = (columnId: string, visible: boolean) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnId]: visible
    }));
  };
  
  // Sort handlers
  const toggleSortTransactionId = () => {
    setSortTransactionId(prev => {
      if (prev === null) return 1; // ascending
      if (prev === 1) return -1; // descending
      return null; // no sort
    });
    setSortTransactionDate(null);
    setSortTotalAmount(null);
  };
  
  const toggleSortTransactionDate = () => {
    setSortTransactionDate(prev => {
      if (prev === null) return 1; // ascending
      if (prev === 1) return -1; // descending
      return null; // no sort
    });
    setSortTransactionId(null);
    setSortTotalAmount(null);
  };
  
  const toggleSortTotalAmount = () => {
    setSortTotalAmount(prev => {
      if (prev === null) return 1; // ascending
      if (prev === 1) return -1; // descending
      return null; // no sort
    });
    setSortTransactionId(null);
    setSortTransactionDate(null);
  };
  
  // Set up the table with filtered columns based on visibility
  const table = useAdvanceTable({
    data: tableData,
    columns: transactionsTableColumns.filter(column => {
      const columnId = typeof column.accessorKey === 'string' 
        ? column.accessorKey 
        : column.id as string;
      return columnVisibility[columnId] !== false;
    }),
    pageSize: pagination.pageSize,
    pagination: true,
    sortable: true,
    rowCount: paginationCount.totalCount,
    manualPagination: true,
    onPaginationChange: (newPagination: { pageIndex: number; pageSize: number }) => {
      // If only the page index changed
      if (newPagination.pageSize === pagination.pageSize) {
        handlePageChange(newPagination.pageIndex);
      } else {
        // If the page size changed
        handlePageSizeChange(newPagination as PaginationState);
      }
    }
  });
  
  // Handle unified search input changes
  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };
  
  // Handle search on Enter key
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSubmittedSearch(searchValue);
    }
  };
  
  // Clear search handler
  const handleClearSearch = () => {
    setSearchValue("");
    setSubmittedSearch("");
  };
  
  const handleClearFromDate = () => {
    setFromDate("");
  };
  
  const handleClearToDate = () => {
    setToDate("");
  };

  // Update error state from the hook
  useEffect(() => {
    if (error) {
      setErrorMessage(t("transactions.loadError", "Failed to load transactions. Please try again."));
    } else {
      setErrorMessage(null);
    }
  }, [error]);
  
  // Check for order_number in location state when component mounts
  useEffect(() => {
    // Get any order_number from the location state
    const orderNumberFromState = 
      location.state && 
      typeof location.state === 'object' && 
      'order_number' in location.state ? 
      (location.state as { order_number: string }).order_number : 
      null;
    
    if (orderNumberFromState) {
      // Clear any existing search first
      setSearchValue('');
      setSubmittedSearch('');
      
      // Use animation to simulate typing the order number
      // This will wait for component to fully render
      setTimeout(() => {
        // Simulate typing animation with order_number - directly updating React state
        // Using a faster typing speed (30ms per character)
        simulateTyping(setSearchValue, orderNumberFromState, 30, () => {
          // After typing is complete, update the submitted search to trigger filtering
          setSubmittedSearch(orderNumberFromState);
        });
      }, 300); // Shorter delay to ensure component is fully rendered
    }
    
    // Cleanup function that runs when component unmounts or when location changes
    return () => {
      // Replace the current history entry but without the state
      if (location.state) {
        window.history.replaceState(null, '', location.pathname);
      }
    };
  }, [location]);

  return (
    <div>
      {/* Toast notification manager */}
      <ToastManager />
      
      <div className="mb-9">
        <h2 className="mb-2">{t("transactions.title", "Transactions")}</h2>
        <p className="text-body-tertiary mb-4">{t("transactions.subtitle", "View and filter your transaction history")}</p>

        {/* Financial KPI Cards - Always shown with loading state */}
        <TransactionKpiCards orderTotals={orderTotals} loading={loading} />

        {/* Error notification */}
        {errorMessage && (
          <ErrorNotification message={errorMessage} onDismiss={() => setErrorMessage(null)} />
        )}

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap align-items-start justify-content-between mb-3">
              <div className="flex-grow-1">
                {/* Transaction Filters Component */}
                <TransactionFilters 
                  ref={searchInputRef}
                  searchValue={searchValue}
                  onSearchChange={handleSearchChange}
                  onSearchKeyPress={handleSearchKeyPress}
                  onClearSearch={handleClearSearch}
                  
                  fromDate={fromDate}
                  toDate={toDate}
                  onFromDateChange={(value) => setFromDate(value)}
                  onToDateChange={(value) => setToDate(value)}
                  onClearFromDate={handleClearFromDate}
                  onClearToDate={handleClearToDate}
                  
                  // Add the customize button inline with filters
                  customizeButton={
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="outline-warning"
                        className="px-7 flex-shrink-0 dropdown-caret-none"
                        style={{
                          backgroundColor: 'transparent',
                          border: '2px solid #ff7f00',
                          color: '#ff7f00',
                          borderRadius: '8px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        }}
                        aria-label={t("transactions.customizeTableAriaLabel", "Customize table columns")}
                      >
                        <FontAwesomeIcon icon={faColumns} className="me-2" />
                        {t("transactions.customizeTable", "Customize Table")}
                        <FontAwesomeIcon
                          icon={faAngleDown}
                          className="ms-2"
                          transform="down-2"
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu
                        style={{
                          padding: '12px',
                          borderRadius: '10px',
                          boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                          minWidth: '200px',
                          backgroundColor: 'var(--bs-body-bg, #ffffff)',
                          border: '1px solid var(--bs-border-color)'
                        }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                      >
                        <div className="mb-2 pb-2 border-bottom">
                          <span className="fw-bold text-body-tertiary">{t("transactions.toggleColumns", "Toggle Columns")}</span>
                        </div>
                        {columnOptions.map((column) => (
                          <ColumnVisibilityToggle
                            key={column.id}
                            id={column.id}
                            label={column.label}
                            checked={column.visible}
                            onChange={(checked) => handleToggleColumn(column.id, checked)}
                          />
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  }
                />
              </div>
            </div>
          </div>

          <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <Spinner animation="border" role="status" variant="warning">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              </div>
            ) : (
              <TransactionsTable totalCount={paginationCount.totalCount} />
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default Transactions;
